<template>
  <div>
    <div v-if="board" class="section board-header-container">
      <div class="container is-fluid">
        <nav class="level board-nav">
          <!-- Left side -->
          <div class="level-left">
            <div class="has-text-left level-item">
              <div class="level">
                <div class="level-left">
                  <div class="level-item">
                    <h2
                      @blur="updateBoardName($event)"
                      @keypress="updateBoardName($event)"
                      :contenteditable="canUpdateBoardInfo"
                      class="has-text-left has-text-white has-text-white-semibold is-size-3"
                    >{{board.name}}</h2>
                  </div>
                  <div @click="toggleUserManagement()" class="clickable level-item">
                    <div v-for="(user,index) in iconUsers" :key="index" class="tooltip">
                      <span
                        :style="{backgroundImage: `url(${user.profile_url})`}"
                        class="icon is-large rounded"
                      ></span>
                      <span class="tooltiptext">{{user.first_name}}</span>
                    </div>
                    <span
                      v-if="board.users.length>4"
                      class="icon rounded has-background-grey is-large"
                    >
                      <span class="has-text-white">+ {{board.users.length-4}}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right side -->
          <div class="level-right">
            <div class="level-item">
              <button
                @click="showNewNoteModal = true"
                class="button has-text-white tooltip new-note-button is-size-5"
              >
                <span class="icon is-medium">
                  <plus-icon size="1.5x" class="custom-class"></plus-icon>
                  <span class="tooltiptext">New Note</span>
                </span>
              </button>
            </div>
            <div class="level-item">
              <button
                @click="toggleUserManagement()"
                class="button tooltip add-user has-text-white is-size-5"
              >
                <span class="icon is-medium">
                  <users-icon size="1.5x" class="custom-class"></users-icon>
                  <span class="tooltiptext">Invite Others</span>

                  <!-- <font-awesome-icon class="is-size-3" icon="users" /> -->
                </span>
              </button>
            </div>
            <div class="level-item">
              <button
                @click="$router.push(`/canvas`)"
                class="tooltip button has-text-white gallery-button is-size-5"
              >
                <span class="icon is-medium">
                  <grid-icon size="1.5x" class="custom-class"></grid-icon>
                  <span class="tooltiptext is-left">Canvas Gallery</span>
                </span>
              </button>
            </div>
          </div>
        </nav>
        <p
          class="has-text-white has-text-left description"
          @blur="updateBoardDescription($event)"
          @keydown="updateBoardDescription($event)"
          :contenteditable="canUpdateBoardInfo"
        >{{board.description}}</p>
        <p
          v-if="overCharacterLimit > 500"
          class="has-text-left has-text-danger"
        >You are {{overCharacterLimit -500}} character{{overCharacterLimit == 501 ? '':'s'}} over the character limit.</p>
      </div>
    </div>
    <section
      :style="{backgroundImage: `url(https://generation.ams3.cdn.digitaloceanspaces.com/web-assets/background${(canvasId % 4)+1}.jpg)`}"
      class="section notes-container"
    >
      <div class="is-fullwidth users-phantom">
        <div
          class="dropdown is-right"
          v-if="showUserManagement"
          v-on-clickaway="hideUserManagement"
          :class="{'is-active':showUserManagement}"
        >
          <div class="dropdown-menu" id="dropdown-menu3" role="menu">
            <div class="dropdown-content">
              <h2 class="has-text-centered is-size-4">Add or Edit Collaborators</h2>
              <div class="user-remove-box" :class="{'is-loading':removingUser}">
                <div
                  v-for="(member,index) in board.users"
                  :key="index + 'assigned'"
                  class="level margin-top"
                >
                  <div class="level-left">
                    <div class="level-item">
                      <span
                        :style="{backgroundImage: `url(${member.profile_url})`}"
                        class="icon is-medium rounded"
                      ></span>
                      <p style="margin-left:4px">{{member.first_name}}</p>
                    </div>
                  </div>
                  <div v-if="member.id != user.id" class="level-right">
                    <div v-if="board.owner_id == user.id" class="level-item">
                      <a @click="removeUser(member.id)" class="is-size-5">
                        <span class="icon is-medium">
                          <user-minus-icon size="1.5x" class="custom-class"></user-minus-icon>
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <hr />
              <button
                class="button add-collaborator"
                :class="{'is-loading':loadingUnassigned}"
                @click="openUserManagement()"
              >View Class</button>
              <div v-if="showUserAdd" class="user-add-box" :class="{'is-loading':addingUser}">
                <div
                  v-for="(member,index) in unassignedUsers"
                  :key="index+'un'"
                  class="level margin-top"
                >
                  <div class="level-left">
                    <div class="level-item">
                      <span
                        :style="{backgroundImage: `url(${member.profile_url})`}"
                        class="icon rounded is-medium"
                      ></span>
                      <p style="margin-left:4px">{{member.first_name}}</p>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="level-item">
                      <a
                        v-if="board.owner_id === user.id"
                        @click="addUser(member.id)"
                        class="is-size-5"
                      >
                        <span class="icon is-medium">
                          <user-plus-icon size="1.5x" class="custom-class"></user-plus-icon>
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="container is-fluid">
        <draggable v-model="notes" group="people" class="columns is-multiline">
          <div
            v-for="note in notes"
            :key="note.id"
            class="column note is-3-desktop is-3-widescreen is-3-fullhd is-4-tablet"
          >
            <div class="box">
              <div class="level pad">
                <div class="level-left">
                  <div class="level-item">
                    <span
                      :style="{backgroundImage: `url(${note.owner.profile_url})`}"
                      class="icon rounded is-medium"
                    ></span>
                    <p style="margin-left:4px">{{note.owner.first_name}}</p>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <div
                      class="dropdown is-up is-right"
                      :class="{'is-active':activeNoteDropUp == note.id}"
                    >
                      <div class="dropdown-trigger">
                        <a @click="toggleActiveNoteDropUp(note.id)">
                          <span class="icon is-medium">
                            <!-- <font-awesome-icon class="is-size-4" icon="ellipsis-v" /> -->
                            <more-vertical-icon size="1.5x" class="custom-class"></more-vertical-icon>
                          </span>
                        </a>
                      </div>
                      <div
                        v-if="activeNoteDropUp == note.id"
                        class="dropdown-menu"
                        id="dropdown-menu7"
                        role="menu"
                      >
                        <div v-on-clickaway="closeExpand" class="dropdown-content">
                          <div class="dropdown-item">
                            <div @click="showNoteExpandModal(note)" class="level clickable">
                              <div class="level-left">
                                <div class="level-item">
                                  <span class="icon is-medium">
                                    <font-awesome-icon class="is-size-4" icon="expand-arrows-alt" />
                                  </span>
                                </div>
                                <div class="level-item">
                                  <p>Expand</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="!isCollapsed(note.id)" class="dropdown-item">
                            <div @click="collapseNote(note.id)" class="level clickable">
                              <div class="level-left">
                                <div class="level-item">
                                  <span class="icon is-medium">
                                    <minimize-2-icon size="2x" class></minimize-2-icon>
                                  </span>
                                </div>
                                <div class="level-item">
                                  <p>Collapse</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-if="isCollapsed(note.id)" class="dropdown-item">
                            <div @click="restoreNote(note.id)" class="level clickable">
                              <div class="level-left">
                                <div class="level-item">
                                  <span class="icon is-medium">
                                    <maximize-2-icon size="2x" class></maximize-2-icon>
                                  </span>
                                </div>
                                <div class="level-item">
                                  <p>Restore</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            v-if="board.owner_id == user.id || note.owner_id == user.id"
                            class="dropdown-item"
                          >
                            <div @click="startDeleteItem(note)" class="level clickable">
                              <div class="level-left">
                                <div class="level-item">
                                  <span class="icon is-medium">
                                    <trash-icon size="2x" class></trash-icon>
                                  </span>
                                </div>
                                <div class="level-item">
                                  <p>Delete</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div @click="showNoteExpandModal(note)" class="clickable note-content">
                <h2
                  :class="{'padding-bottom':isCollapsed(note.id)}"
                  class="pad has-text-left note-title is-size-5"
                >{{note.name}}</h2>
                <hr :class="{'is-hidden':isCollapsed(note.id)}" />
                <div
                  :class="{'is-hidden':isCollapsed(note.id)}"
                  class="pad margin-top has-text-left"
                  v-html="clipText(note.content)"
                ></div>
                <hr :class="{'is-hidden':isCollapsed(note.id)}" />
              </div>
              <div class="media-content" :class="{'is-hidden':isCollapsed(note.id)}">
                <div class="file pad margin-top" :class="{'is-loading':uploadingFile == note.id}">
                  <label class="clickable">
                    <input
                      class="attach-media clickable"
                      @change="uploadFile($event,note.id)"
                      type="file"
                    />
                    <paperclip-icon size="1.5x" class="clickable"></paperclip-icon>
                  </label>
                </div>
                <p
                  @click="showNoteExpandModal(note)"
                  v-if="note.medias.length>2"
                  class="clickable has-text-grey-light pad has-text-weight-semibold is-size-7"
                >View All Media</p>
                <div
                  v-for="media in limitItems(note.medias)"
                  class="pad media-level level"
                  :key="media.id + media"
                >
                  <div class="level-left media-level-left">
                    <div class="level-item">
                      <a target="_blank" :href="media.url">{{media.filename}}</a>
                    </div>
                  </div>
                  <div class="level-right">
                    <div
                      @click="startDeleteMedia(note,media.id)"
                      v-if="canEditNote(media.created_user_id)"
                      class="clickable level-item"
                    >
                      <span class="icon is-small">
                        <trash-icon size="1x" class></trash-icon>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div
                :class="{'is-hidden':isCollapsed(note.id)}"
                class="margin-top has-text-left bottom pad comments-container"
              >
                <p
                  @click="showNoteExpandModal(note)"
                  v-if="note.comments.length>2"
                  class="clickable has-text-centered has-text-grey-light pad has-text-weight-semibold is-size-7"
                >View All Comments</p>
                <div
                  v-for="comment in limitItems(note.comments)"
                  class="comments"
                  :class="{'is-loading':deletingComment == comment.id}"
                  :key="comment.id+'comment'"
                >
                  <article class="media">
                    <figure class="media-left">
                      <span
                        :style="{backgroundImage: `url(${comment.creator.profile_url})`}"
                        class="icon rounded is-medium"
                      ></span>
                    </figure>
                    <div class="media-content">
                      <div class="content">
                        <p class="is-size-6 no-margin-bottom">
                          <small class="is-size-7">{{comment.creator.first_name}}</small>
                          <br />
                          <span
                            v-if="comment.body.length <=120 || expandedComments.includes(comment.id)"
                            v-html="comment.body"
                          ></span>
                          <span
                            v-if="comment.body.length >120 && !expandedComments.includes(comment.id)"
                            v-html="clipComment(comment.body)"
                          ></span>
                        </p>
                        <p
                          v-if="comment.body.length >120 && !expandedComments.includes(comment.id)"
                          @click="expandComment(comment.id)"
                          class="clickable has-text-centered has-text-grey-light pad has-text-weight-semibold is-size-7"
                        >Expand Comment</p>
                        <p
                          v-if="expandedComments.includes(comment.id)"
                          @click="collapseComment(comment.id)"
                          class="clickable has-text-centered has-text-grey-light pad has-text-weight-semibold is-size-7"
                        >Collapse Comment</p>
                      </div>
                    </div>
                    <div v-if="comment.creator_id == user.id" class="media-right">
                      <span
                        @click="deleteComment(comment.id,note.id)"
                        class="clickable icon is-small"
                      >
                        <trash-icon size="1x" class></trash-icon>
                      </span>
                    </div>
                  </article>
                </div>
                <!-- :class="{'hide-toolbar':shownToolbar != note.id}" -->
                <div class="columns is-vcentered is-mobile">
                  <div class="column is-2">
                    <span
                      :style="{backgroundImage: `url(${user.profile_image})`}"
                      class="icon rounded is-medium"
                    ></span>
                  </div>

                  <div class="column">
                    <!-- <ckeditor
                      @focus="shownToolbar = note.id"
                      @blur="shownToolbar = null"
                      :config="miniConfig"
                      :editor="editor"
                      @ready="loadEditor"
                      v-model="newComments[note.id]"
                    ></ckeditor>-->
                    <form
                      :class="{'is-loading':creatingComment == note.id}"
                      @submit.prevent="createComment(note.id)"
                      class="comment-form"
                    >
                      <input
                        class="input"
                        type="text"
                        v-model="newComments[note.id]"
                        placeholder="Add comment"
                        :ref="'comment'+note.id"
                      />
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </draggable>
      </div>
    </section>

    <div class="modal" :class="{'is-active':showNewNoteModal}">
      <div class="modal-background" @click.prevent="showNewNoteModal = false"></div>
      <div class="modal-content note-modal section box">
        <div class="columns is-centered">
          <div class="column">
            <h2 class="has-text-centered">Create a Note</h2>
            <form
              class="has-text-left create-form"
              :class="{'is-loading':creatingNote}"
              @submit.prevent="createNote()"
            >
              <div class="field">
                <label class="label">Title</label>
                <div class="control">
                  <input
                    class="input"
                    type="text"
                    v-model="newNote.name"
                    required
                    placeholder="Title"
                  />
                </div>
              </div>
              <div class="field">
                <label class="label">Content</label>
                <div class="control">
                  <ckeditor
                    :config="config"
                    :editor="editor"
                    @ready="loadEditor"
                    v-model="newNote.content"
                  ></ckeditor>
                </div>
              </div>
              <div class="has-text-centered columns is-centered is-mobile field">
                <div class="column">
                  <input type="submit" class="button is-link" />
                </div>
                <div class="column">
                  <button
                    @click.prevent="cancelCreateNote()"
                    class="button is-link is-warning"
                  >Cancel</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <button
        @click.prevent="showNewNoteModal = false"
        class="modal-close is-large"
        aria-label="close"
      ></button>
    </div>
    <div class="modal" v-if="activeNoteExpand" :class="{'is-active':activeNoteExpand}">
      <div class="modal-background" @click.prevent="closeNoteExpand()"></div>
      <div class="modal-content active-note-modal section box">
        <div class="columns is-centered">
          <div class="column note">
            <div class="box">
              <div class="note-content">
                <h2
                  :contenteditable="canEditNote(activeNote.owner_id)"
                  @blur="updateActiveNoteName($event)"
                  @keypress="updateActiveNoteName($event)"
                  class="pad has-text-left note-title is-size-5"
                >{{activeNote.name}}</h2>
                <hr />
                <div
                  class="pad margin-top edit-note-content has-text-left"
                  :class="{'is-loading':savingChanges}"
                >
                  <ckeditor
                    v-if="canEditNote(activeNote.owner_id)"
                    :config="config"
                    :editor="editor"
                    @ready="loadEditor"
                    v-model="bufferActiveNoteContent"
                  ></ckeditor>
                  <div v-else v-html="activeNote.content"></div>
                </div>
                <div v-if="canEditNote(activeNote.owner_id)" class="pad has-text-right">
                  <button
                    :class="{'is-loading':savingChanges}"
                    class="button margin-top is-link"
                    @click.prevent="saveChanges()"
                  >Save</button>
                </div>
                <hr />
                <div class="media-content">
                  <div
                    class="file pad margin-top"
                    :class="{'is-loading':uploadingFile == activeNote.id}"
                  >
                    <label class="clickable">
                      <input
                        class="attach-media clickable"
                        @change="uploadFile($event,activeNote.id)"
                        type="file"
                      />
                      <paperclip-icon size="1.5x" class="clickable"></paperclip-icon>
                    </label>
                  </div>
                  <div
                    v-for="media in activeNote.medias"
                    class="pad media-level level"
                    :key="media.id + media"
                  >
                    <div class="media-level-left level-left">
                      <div class="level-item">
                        <a target="_blank" :href="media.url">{{media.filename}}</a>
                      </div>
                    </div>
                    <div class="level-right">
                      <div
                        v-if="canEditNote(media.created_user_id)"
                        @click="startDeleteMedia(activeNote,media.id)"
                        class="clickable level-item"
                      >
                        <span class="icon is-small">
                          <trash-icon size="1x" class></trash-icon>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="margin-top has-text-left bottom pad comments-container">
                <div
                  v-for="comment in activeNote.comments"
                  class="comments"
                  :class="{'is-loading':deletingComment == comment.id}"
                  :key="comment.id+'comment'"
                >
                  <article class="media">
                    <figure class="media-left">
                      <span
                        :style="{backgroundImage: `url(${comment.creator.profile_url})`}"
                        class="icon rounded is-medium"
                      ></span>
                    </figure>
                    <div class="media-content">
                      <div class="content">
                        <p class="is-size-6 no-margin-bottom">
                          <small class="is-size-7">{{comment.creator.first_name}}</small>
                          <br />
                          <span
                            v-if="comment.body.length <=120 || expandedComments.includes(comment.id)"
                            v-html="comment.body"
                          ></span>
                          <span
                            v-if="comment.body.length >120 && !expandedComments.includes(comment.id)"
                            v-html="clipComment(comment.body)"
                          ></span>
                        </p>
                        <p
                          v-if="comment.body.length >120 && !expandedComments.includes(comment.id)"
                          @click="expandComment(comment.id)"
                          class="clickable has-text-centered has-text-grey-light pad has-text-weight-semibold is-size-7"
                        >Expand Comment</p>
                        <p
                          v-if="expandedComments.includes(comment.id)"
                          @click="collapseComment(comment.id)"
                          class="clickable has-text-centered has-text-grey-light pad has-text-weight-semibold is-size-7"
                        >Collapse Comment</p>
                      </div>
                    </div>
                    <div v-if="comment.creator_id == user.id" class="media-right">
                      <span
                        @click="deleteComment(comment.id,activeNote.id)"
                        class="clickable icon is-small"
                      >
                        <trash-icon size="1x" class></trash-icon>
                      </span>
                    </div>
                  </article>
                </div>
                <!-- :class="{'hide-toolbar':shownToolbar != note.id}" -->
                <div class="columns is-vcentered is-mobile">
                  <div class="column is-2">
                    <span
                      :style="{backgroundImage: `url(${activeNote.owner.profile_url})`}"
                      class="icon rounded is-medium"
                    ></span>
                  </div>

                  <div class="column">
                    <!-- <ckeditor
                      @focus="shownToolbar = note.id"
                      @blur="shownToolbar = null"
                      :config="miniConfig"
                      :editor="editor"
                      @ready="loadEditor"
                      v-model="newComments[note.id]"
                    ></ckeditor>-->
                    <form
                      :class="{'is-loading':creatingComment == activeNote.id}"
                      class="comment-form"
                      @submit.prevent="createComment(activeNote.id)"
                    >
                      <input
                        class="input"
                        type="text"
                        v-model="newComments[activeNote.id]"
                        placeholder="Add comment"
                        :ref="'comment'+activeNote  .id"
                      />
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button @click.prevent="closeNoteExpand()" class="modal-close is-large" aria-label="close"></button>
    </div>
    <div class="modal" :class="{'is-active':noteBeingDeleted}">
      <div class="modal-background" @click.prevent="noteBeingDeleted = null"></div>
      <div class="modal-content note-modal section box">
        <div class="columns is-centered">
          <div class="column is-6">
            <h2>Deleting Note</h2>
            <p>Are you sure you would like to delete this item? This action cannot be undone.</p>
            <button @click.prevent="noteBeingDeleted = null" class="button is-link">Cancel</button>
            <button
              :class="{'is-loading':deletingNote}"
              @click.prevent="deleteNote()"
              class="button is-danger"
            >Confirm</button>
          </div>
        </div>
      </div>
      <button
        @click.prevent="noteBeingDeleted = null"
        class="modal-close is-large"
        aria-label="close"
      ></button>
    </div>
    <div class="modal" :class="{'is-active':mediaBeingdeleted}">
      <div class="modal-background" @click.prevent="mediaBeingdeleted = null"></div>
      <div class="modal-content note-modal section box">
        <div class="columns is-centered">
          <div class="column is-6">
            <h2>Deleting Media</h2>
            <p>Are you sure you would like to delete this item? This action cannot be undone.</p>
            <button @click.prevent="mediaBeingdeleted = null" class="button is-link">Cancel</button>
            <button
              :class="{'is-loading':deletingMedia}"
              @click.prevent="deleteMedia()"
              class="button is-danger"
            >Confirm</button>
          </div>
        </div>
      </div>
      <button
        @click.prevent="mediaBeingdeleted = null"
        class="modal-close is-large"
        aria-label="close"
      ></button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Board from "@/models/Board";
import BoardUser from "@/models/BoardUser";
import Note from "@/models/Note";
import User from "@/models/User";
import Media from "@/models/Media";
import Comment from "@/models/Comment";
import CKEditor from "@ckeditor/ckeditor5-vue";
import DecoupledEditor from "@ckeditor/ckeditor5-build-decoupled-document";
import draggable from "vuedraggable";
var clip = require("text-clipper");
import { mixin as clickaway } from "vue-clickaway";
import { GroupsClient } from "@/clients/GroupsClient";
import {
  MoreVerticalIcon,
  PaperclipIcon,
  TrashIcon,
  PlusIcon,
  UsersIcon,
  UserMinusIcon,
  UserPlusIcon,
  GridIcon,
  Minimize2Icon,
  Maximize2Icon
} from "vue-feather-icons";

export default {
  name: "Board",
  props: ["canvasId"],
  mixins: [clickaway],

  components: {
    ckeditor: CKEditor.component,
    draggable: draggable,
    MoreVerticalIcon,
    PaperclipIcon,
    TrashIcon,
    PlusIcon,
    UsersIcon,
    UserMinusIcon,
    UserPlusIcon,
    GridIcon,
    Minimize2Icon,
    Maximize2Icon
  },
  data: function() {
    return {
      overCharacterLimit: true,
      collapsedNotes: [],
      expandedComments: [],
      bufferActiveNoteName: "",
      bufferActiveNoteContent: "",
      savingChanges: false,
      shownToolbar: null,
      creatingComment: null,
      newComments: [],
      editor: DecoupledEditor,
      config: {
        placeholder: "Type your content here",

        toolbar: [
          "fontSize",
          "fontFamily",
          "|",
          "bold",
          "italic",
          "underline",
          "strikethrough",
          "highlight",
          "|",
          "alignment",
          "|",
          "bulletedList",
          "numberedList",
          "insertTable",
          "|",
          "undo",
          "redo"
        ]
      },
      miniConfig: {
        placeholder: "Add comment!",
        toolbar: [
          "bold",
          "italic",
          "underline",
          "highlight",
          "bulletedList",
          "numberedList"
        ]
      },
      deletingComment: null,
      uploadingFile: null,
      mediaBeingdeleted: null,
      deletingMedia: false,
      deletingNote: false,
      noteBeingDeleted: null,
      activeNoteExpand: false,
      activeNoteId: 0,
      activeNoteDropUp: null,
      updatingBoardInfo: false,
      creatingNote: false,
      removingUser: false,
      addingUser: false,
      loadingUnassigned: false,
      showUserManagement: false,
      showUserAdd: false,
      classUsers: [],
      newNote: {
        content: "",
        name: "",
        order: 0
      },
      showNewNoteModal: false
    };
  },
  computed: {
    iconUsers() {
      return this.board.users.length > 4
        ? this.board.users.slice(0, 4)
        : this.board.users;
    },
    ...mapState({
      user(state) {
        return state.auth.user;
      }
    }),
    activeNote() {
      return Note.query()
        .whereId(this.activeNoteId)
        .with("owner")
        .with("medias")
        .with("comments.creator")
        .first();
    },
    unassignedUsers() {
      return User.query()
        .where(user => {
          if (this.board.users.some(e => e.id == user.id)) {
            return false;
          }
          return true;
        })
        .get();
    },
    board() {
      return Board.query()
        .with("owner")
        .with("users")
        .where("id", this.canvasId)
        .first();
    },
    notes: {
      get() {
        return Note.query()
          .with("owner")
          .with("medias")
          .with("comments.creator")
          .where("board_id", parseInt(this.canvasId))
          .orderBy("order", "asc")
          .get();
      },
      set(evt) {
        let orderStore = [];
        evt.map(function(currentelement, index, arrayobj) {
          if (currentelement.order != index) {
            currentelement.order = index;
            Note.updateNote(currentelement);

            orderStore.push(currentelement);
          }
        });
        Note.update({
          data: orderStore
        });
      }
    },
    canUpdateBoardInfo() {
      if (!this.updatingBoardInfo && this.user.id == this.board.owner.id) {
        return true;
      }
      return false;
    }
  },
  methods: {
    isCollapsed(note_id) {
      return this.collapsedNotes.includes(note_id);
    },
    restoreNote(note_id) {
      this.collapsedNotes.splice(this.collapsedNotes.indexOf(note_id));
    },
    collapseNote(note_id) {
      this.collapsedNotes.push(note_id);
    },
    expandComment(comment_id) {
      this.expandedComments.push(comment_id);
    },
    collapseComment(comment_id) {
      this.expandedComments.splice(this.expandedComments.indexOf(comment_id));
    },
    canEditNote(owner_id) {
      if (this.user.id == owner_id) {
        return true;
      }
      return false;
    },
    limitItems(items) {
      if (items.length < 3) {
        return items;
      } else {
        return items.slice(-2);
      }
    },
    clipText(text) {
      return clip(text, 300, {
        html: true,
        indicator: `<br/><span class="has-text-grey-light is-italic">Expand to see more</span>`
      });
    },
    clipComment(text) {
      return clip(text, 120, {
        html: true
      });
    },
    saveChanges() {
      this.savingChanges = true;
      let note = this.activeNote;
      note.content = this.bufferActiveNoteContent;
      note.name = this.bufferActiveNoteName;
      Note.updateNote(note).then(() => {
        Note.showNote(this.canvasId, note.id, [
          "owner",
          "media",
          "comments"
        ]).then(() => {
          this.savingChanges = false;
        });
      });
    },
    createComment(note_id) {
      this.creatingComment = note_id;
      Note.attachComment(
        this.canvasId,
        note_id,
        this.newComments[note_id]
      ).then(() => {
        this.newComments[note_id] = null;
        Note.showNote(this.canvasId, note_id, ["owner", "media", "comments"]);
        this.creatingComment = null;
      });
    },
    uploadFile(evt, note_id) {
      this.uploadingFile = note_id;
      let formData = new FormData();
      formData.append("file", evt.target.files[0]);
      formData.append("type", "image");

      Note.attachFile(this.canvasId, note_id, formData).then(() => {
        Note.showNote(this.canvasId, note_id, ["owner", "media"]).then(() => {
          this.uploadingFile = null;
        });
      });
    },
    deleteNote() {
      this.deletingNote = true;
      Note.deleteNote(this.canvasId, this.noteBeingDeleted.id).then(() => {
        Note.listNotes(this.canvasId, 1, 999, ["owner"]).then(() => {
          this.deletingNote = false;
          Note.delete(this.noteBeingDeleted.id);
          this.noteBeingDeleted = null;
        });
      });
    },

    deleteMedia() {
      this.deletingMedia = true;
      Note.deleteFile(
        this.canvasId,
        this.mediaBeingdeleted.note.id,
        this.mediaBeingdeleted.media_id
      ).then(() => {
        Media.delete(this.mediaBeingdeleted.media_id);
        this.deletingMedia = false;
        this.mediaBeingdeleted = null;
      });
    },
    deleteComment(comment_id, note_id) {
      this.deletingComment = comment_id;
      Note.deleteComment(this.canvasId, note_id, comment_id).then(() => {
        Comment.delete(comment_id);
        this.deletingComment = null;
      });
    },
    startDeleteMedia(note, media_id) {
      this.mediaBeingdeleted = { note: note, media_id: media_id };
      this.clearActiveNote();
    },
    closeExpand() {
      this.activeNoteDropUp = null;
    },
    startDeleteItem(note) {
      this.noteBeingDeleted = note;
      this.clearActiveNote();
    },
    loadEditor(editor) {
      editor.ui
        .getEditableElement()
        .parentElement.insertBefore(
          editor.ui.view.toolbar.element,
          editor.ui.getEditableElement()
        );
      // editor.keystrokes.set("Ctrl+enter", (data, stop) => {
      //   console.log(data);
      //   data.preventDefault();
      // });
      // console.log(Array.from(editor.ui.componentFactory.names()));
    },
    clearActiveNote() {
      this.activeNoteDropUp = null;
    },
    closeNoteExpand() {
      this.activeNoteExpand = false;
    },
    showNoteExpandModal(note) {
      this.activeNoteId = note.id;
      this.bufferActiveNoteContent = this.activeNote.content;
      this.bufferActiveNoteName = this.activeNote.name;
      this.activeNoteExpand = true;

      this.clearActiveNote();
    },
    toggleUserManagement() {
      this.showUserManagement = !this.showUserManagement;
    },
    toggleActiveNoteDropUp(note_id) {
      if (this.activeNoteDropUp == note_id) {
        this.activeNoteDropUp = null;
      } else {
        this.activeNoteDropUp = note_id;
      }
    },
    updateActiveNoteName($event) {
      if ($event.charCode === 13 || $event.type == "blur") {
        $event.preventDefault();
        let src = $event.target.innerText;
        this.bufferActiveNoteName = src;
        this.saveChanges();
      }
    },

    updateBoardName($event) {
      if ($event.charCode === 13 || $event.type == "blur") {
        $event.preventDefault();
        let src = $event.target.innerText;
        this.updatingBoardInfo = true;
        Board.update({
          where: parseInt(this.canvasId),
          data: { name: src }
        });
        Board.updateBoard({
          name: src,
          owner_id: this.user.id,
          is_public: this.board.is_public,
          id: this.canvasId,
          description: this.board.description
        }).then(() => {
          this.updatingBoardInfo = false;
        });
      }
    },
    updateBoardDescription($event) {
      this.overCharacterLimit = $event.target.innerText.length;

      if ($event.charCode === 13 || $event.type == "blur") {
        $event.preventDefault();
        let src = $event.target.innerText;
        this.updatingBoardInfo = true;
        Board.update({
          where: parseInt(this.canvasId),
          data: { description: src }
        });
        Board.updateBoard({
          name: this.board.name,
          owner_id: this.user.id,
          is_public: this.board.is_public,
          id: this.canvasId,
          description: src
        }).then(() => {
          this.updatingBoardInfo = false;
        });
      }
    },
    removeUser(user_id) {
      this.removingUser = true;
      Board.removeBoardUser(this.canvasId, user_id).then(() => {
        BoardUser.delete([this.canvasId, user_id]);
        this.removingUser = false;
      });
    },
    addUser(user_id) {
      this.addingUser = true;
      Board.addBoardUser(this.canvasId, user_id).then(() => {
        Board.showBoardUsers(this.canvasId).then(() => {
          this.addingUser = false;
        });
      });
    },
    loadUsers() {
      this.loadingUnassigned = true;
      User.showGroupUsers(this.board.group_id)
        .then(result => {
          this.loadingUnassigned = false;
        })
        .catch(err => {});
    },
    openUserManagement() {
      this.showUserAdd = true;
    },
    hideUserManagement() {
      this.showUserManagement = false;
      this.showUserAdd = false;
    },

    cancelCreateNote() {
      this.clearForm();
      this.showNewNoteModal = false;
    },

    clearForm() {
      Object.keys(this.newNote).forEach(key => {
        this.newNote[key] = "";
      });
      this.newNote.order = 0;
    },
    createNote() {
      this.creatingNote = true;
      this.newNote.owner_id = this.user.id;
      this.newNote.board_id = parseInt(this.canvasId);
      if (this.notes.length > 0) {
        this.newNote.order = this.notes[this.notes.length - 1].order + 1;
      }
      Note.createNote(this.newNote)
        .then(result => {
          Note.listNotes(this.canvasId, 1, 999, ["owner"]).then(() => {
            this.creatingNote = false;
            this.showNewNoteModal = false;
            this.clearForm();
          });
        })
        .catch(err => {});
    }
  },
  mounted() {
    this.$store.dispatch("loader/show", "Loading Canvas");

    Board.showBoard(this.canvasId).then(() => {
      Board.showBoardUsers(this.canvasId)
        .then(() => {
          this.$store.dispatch("loader/hide");
          this.loadUsers();
        })
        .catch(err => {});
    });
    Note.listNotes(this.canvasId, 1, 999, ["owner", "media", "comments"]);
    window.Echo.private(`Board.${this.canvasId}`)
      .listen(".Modules\\Canvas\\Events\\BoardUpdated", e => {
        Object.assign(e, e.attributes);

        Board.update({
          where: e.id,
          data: {
            name: e.name,
            description: e.description,
            is_public: e.is_public
          }
        });
      })
      .listen(".Modules\\Canvas\\Events\\NoteCreated", e => {
        Object.assign(e, e.attributes);

        Note.insert({ data: e });
      })
      .listen(".Modules\\Canvas\\Events\\NoteUpdated", e => {
        Object.assign(e, e.attributes);

        Note.update({ where: e.id, data: e });
      })
      .listen(".Modules\\Canvas\\Events\\UserAdded", e => {
        let storageArray = [];
        let target = e;
        target.id = parseInt(target.id);
        Object.assign(target, target.attributes);
        storageArray.push(target);

        Board.insertOrUpdate({
          data: {
            id: this.canvasId,
            users: storageArray
          }
        });
      })
      .listen(".Modules\\Canvas\\Events\\UserRemoved", e => {
        BoardUser.delete([this.canvasId, parseInt(e.id)]);
      })
      .listen(".Modules\\Canvas\\Events\\NoteDeleted", e => {
        Note.delete(parseInt(e.id));
      })
      .listen(".Modules\\Canvas\\Events\\CommentCreated", e => {
        e.note_id = e.commentable_id;

        Comment.insert({
          data: e
        });
      })
      .listen(".Modules\\Canvas\\Events\\CommentDeleted", e => {
        Comment.delete(parseInt(e.id));
      })
      .listen(".Modules\\Canvas\\Events\\MediaCreated", e => {
        Media.insert({ data: e });
      })
      .listen(".Modules\\Canvas\\Events\\MediaDeleted", e => {
        Media.delete(parseInt(e.id));
      });
  }
};
</script>

